import { useEffect, useRef } from "react";

import type { Locale } from "../../../shared/types";
import { useCopy } from "../lib/copy";

interface DockProps {
  locale: Locale;
  value: string;
  /**
   * A turn is in flight. This changes the hint and holds the microphone, and that
   * is ALL it does — it must never disable the text field. Locking the keyboard for
   * the five seconds a reply takes is indistinguishable from the page having frozen,
   * which is exactly what it was reported as.
   */
  busy: boolean;
  recording: boolean;
  micAvailable: boolean;
  micDenied: boolean;
  /** A finding has just landed, and the dock answers with one pop. */
  commit: boolean;
  statusId: string;
  onChange: (value: string) => void;
  onSend: () => void;
  onMicToggle: () => void;
}

function MicIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <rect x="9" y="3" width="6" height="11" rx="3" />
      <path d="M5 11a7 7 0 0 0 14 0" />
      <path d="M12 18v3" />
    </svg>
  );
}

export default function Dock({
  locale,
  value,
  busy,
  recording,
  micAvailable,
  micDenied,
  commit,
  statusId,
  onChange,
  onSend,
  onMicToggle,
}: DockProps) {
  const copy = useCopy(locale);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // The dock is the default focus target, but only where focusing it does not throw a
  // software keyboard over half the screen on arrival.
  useEffect(() => {
    if (window.matchMedia("(pointer: fine)").matches) inputRef.current?.focus();
  }, []);

  // Trimmed, not raw: three spaces in the field made the hint promise RETURN TO SEND
  // while Return did nothing at all, because `onSend` refuses whitespace.
  const hint = recording
    ? copy.hintRelease
    : busy
      ? copy.hintThinking
      : value.trim().length > 0
        ? copy.hintSend
        : copy.hintTalk;

  const micClass = ["dock__mic", "btn", "btn--icon"];
  if (recording) micClass.push("dock__mic--recording");
  if (micDenied) micClass.push("dock__mic--denied");

  return (
    <form
      className={commit ? "dock dock--commit" : "dock"}
      onSubmit={(event) => {
        event.preventDefault();
        // Not gated on `busy`: sending mid-reply is cutting in, and useTurn.send
        // already aborts the turn in flight and starts a new one.
        if (recording) return;
        onSend();
      }}
    >
      {micAvailable && (
        <button
          type="button"
          className={micClass.join(" ")}
          // Denied is deliberately not aria-disabled: allowing the microphone from the
          // address bar and pressing again is the way back, so the control stays live.
          // The reason is in the status line this points at.
          aria-pressed={recording}
          // Not `micLabel`: hold-to-talk is real on the SPACE key and nowhere else.
          // This button starts on one click and stops on the next, so it says so.
          aria-label={copy.micButton}
          aria-describedby={statusId}
          onClick={onMicToggle}
        >
          <MicIcon />
        </button>
      )}

      <label htmlFor="dock-input" className="sr-only">
        {copy.inputPlaceholder}
      </label>
      <input
        id="dock-input"
        ref={inputRef}
        className="dock__input"
        type="text"
        name="message"
        autoComplete="off"
        enterKeyHint="send"
        placeholder={copy.inputPlaceholder}
        readOnly={recording}
        aria-describedby={statusId}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />

      {/* Duplicates keyboard affordances that are already discoverable; reading it out
          on every focus would be noise. */}
      <span className="dock__hint" aria-hidden="true">
        {hint}
      </span>
    </form>
  );
}
